import React, { useEffect, useState } from 'react';
import { formatPhoneInput } from '@/lib/utils';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Gift, Trophy, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  en: {
    pick: 'Choose your language', enter: 'Enter the raffle', entered: "You're entered!",
    enteredSub: "We'll announce the results soon — your agent will reach out shortly.",
    first: 'First name', last: 'Last name', phone: 'Mobile phone', email: 'Email (optional)',
    consent: 'I agree to receive SMS text messages about this raffle and a free coverage review. Message & data rates may apply. Reply STOP to opt out.',
    submit: 'Enter raffle', hostedBy: 'Hosted by', required: 'Please complete name, phone and consent.',
    closed: 'This raffle is no longer accepting entries.',
  },
  es: {
    pick: 'Elija su idioma', enter: 'Participe en la rifa', entered: '¡Ya está inscrito!',
    enteredSub: 'Pronto sabremos los resultados — su agente se comunicará con usted en breve.',
    first: 'Nombre', last: 'Apellido', phone: 'Teléfono móvil', email: 'Correo (opcional)',
    consent: 'Acepto recibir mensajes de texto SMS sobre esta rifa y una revisión de cobertura gratuita. Pueden aplicar tarifas de mensajes y datos. Responda STOP para darse de baja.',
    submit: 'Participar', hostedBy: 'Organizado por', required: 'Complete nombre, teléfono y consentimiento.',
    closed: 'Esta rifa ya no acepta inscripciones.',
  },
};

export default function PublicRafflePage() {
  const { token } = useParams();
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(null); // null → show language prompt first
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/raffles/public/${token}`)
      .then(({ data }) => setRaffle(data))
      .catch(() => setError('not_found'))
      .finally(() => setLoading(false));
  }, [token]);

  const t = T[lang || 'en'];

  const submit = async () => {
    if (!form.first_name.trim() || !form.phone.trim() || !form.consent) { setError(t.required); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/raffles/public/${token}/enter`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || null,
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        preferred_language: lang || 'en',
        consent: form.consent,
      });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[#0f2547]"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>;
  }
  if (error === 'not_found' || !raffle) {
    return <div className="min-h-screen grid place-items-center bg-[#0f2547] text-white px-6 text-center">Raffle not found.</div>;
  }

  return (
    <div className="min-h-screen bg-[#0f2547] text-white px-4 py-10 flex flex-col items-center" data-testid="public-raffle-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex h-14 w-14 rounded-2xl bg-[#f5c14f] items-center justify-center mb-3">
            <Gift className="h-7 w-7 text-[#0f2547]" />
          </div>
          <h1 className="text-2xl font-bold">{raffle.name}</h1>
          <p className="mt-1 text-[#f5c14f] flex items-center justify-center gap-1.5"><Trophy size={15} /> {raffle.prize}</p>
          <p className="mt-1 text-xs text-white/60">{t.hostedBy} {raffle.agent_name}</p>
        </div>

        {!raffle.active ? (
          <div className="rounded-2xl bg-white/10 p-6 text-center" data-testid="public-raffle-closed">{t.closed}</div>
        ) : done ? (
          <div className="rounded-2xl bg-white text-[#0f2547] p-8 text-center" data-testid="public-raffle-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold">{t.entered}</h2>
            <p className="mt-2 text-sm text-slate-600">{t.enteredSub}</p>
          </div>
        ) : lang === null ? (
          <div className="rounded-2xl bg-white/10 p-6" data-testid="public-raffle-lang">
            <p className="text-center text-sm text-white/80 mb-4">{T.en.pick} · {T.es.pick}</p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setLang('en')} className="rounded-xl bg-white text-[#0f2547] font-semibold py-3 hover:bg-white/90" data-testid="public-raffle-lang-en">English</button>
              <button onClick={() => setLang('es')} className="rounded-xl bg-[#f5c14f] text-[#0f2547] font-semibold py-3 hover:brightness-95" data-testid="public-raffle-lang-es">Español</button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl bg-white text-[#0f2547] p-5 space-y-3" data-testid="public-raffle-form">
            <div className="flex justify-end -mt-1 -mr-1">
              <button onClick={() => setLang(lang === 'en' ? 'es' : 'en')} className="text-xs text-slate-500 underline" data-testid="public-raffle-lang-toggle">
                {lang === 'en' ? 'Español' : 'English'}
              </button>
            </div>
            <Input label={t.first} value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="public-raffle-first" />
            <Input label={t.last} value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="public-raffle-last" />
            <Input label={t.phone} type="tel" value={formatPhoneInput(form.phone)} onChange={(v) => setForm({ ...form, phone: v })} testid="public-raffle-phone" />
            <Input label={t.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="public-raffle-email" />
            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 h-4 w-4" data-testid="public-raffle-consent" />
              <span>{t.consent}</span>
            </label>
            {error && <p className="text-sm text-rose-600" data-testid="public-raffle-error">{error}</p>}
            <button onClick={submit} disabled={submitting} className="w-full rounded-xl bg-[#0f2547] text-white font-semibold py-3 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="public-raffle-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift size={16} />} {t.submit}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const Input = ({ label, value, onChange, type = 'text', testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0f2547] focus:outline-none text-sm"
      data-testid={testid}
    />
  </div>
);
